/**
 * 日期格式化工具模块 - 无副作用设计
 *
 * 设计原则：
 * 1. 所有函数都是纯函数，不修改任何全局状态
 * 2. locale 参数显式传入，不依赖 dayjs 全局 locale
 * 3. 提供 Intl API 后备方案，确保浏览器兼容性
 *
 * 使用场景：
 * - 邮件编辑器内容块渲染（需要保证英文输出）
 * - 航班计划导出（需要遵循航空业标准格式）
 * - 任何需要确定性 locale 输出的场景
 */

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
// 预加载所需的 locale（模块级，不影响全局 locale 设置）
import "dayjs/locale/en";
import "dayjs/locale/zh-cn";

// 扩展 dayjs 插件
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * 航空业日期格式化常量
 * 基于 IATA/ICAO 标准格式
 */
export const AVIATION_DATE_FORMATS = {
    DD_MMM: "DD-MMM", // 04-Dec (英文) / 04-12月 (中文)
    DDMMM: "DDMMM", // 04Dec (英文，无分隔符)
    DDMMM_UPPER: "DDMMM", // 04DEC (大写，需后处理 .toUpperCase())
    YYYY_MM_DD: "YYYY-MM-DD", // ISO 标准格式
    HH_MM: "HH:mm", // 时间格式
    MONTH_YEAR: "MMMM YYYY", // December 2025
    SHORT_MONTH_YEAR: "MMM YYYY", // Dec 2025
    M_D_CN: "M月D日", // 中文日期格式
    MM_DD_HH_MM: "MM-DD HH:mm", // 01-15 08:30
};

/**
 * Locale 常量
 */
export const LOCALE_OPTIONS = {
    EN: "en",
    ZH_CN: "zh-cn",
};

/**
 * 规范化 UTC 时间字符串
 * 确保时间字符串被正确识别为 UTC
 *
 * @param {string} dateString - 日期字符串
 * @returns {string} 规范化后的 UTC 时间字符串
 */
function normalizeUTCString(dateString) {
    if (typeof dateString !== "string") return dateString;

    // 如果包含 T 但不以 Z 结尾且不包含时区偏移，添加 Z
    if (dateString.includes("T") && !dateString.endsWith("Z") && !dateString.includes("+") && !dateString.match(/-\d{2}:\d{2}$/)) {
        return dateString + "Z";
    }

    return dateString;
}

/**
 * 格式化 UTC 日期为指定 locale
 * 这是核心的纯函数 —— 无副作用，所有参数显式传入
 *
 * @param {string|Date} dateString - UTC 日期字符串
 * @param {string} format - 输出格式 (dayjs format string)
 * @param {string} locale - 目标 locale ('en' | 'zh-cn')
 * @returns {string} 格式化后的日期字符串
 */
export function formatDateWithLocale(dateString, format, locale = LOCALE_OPTIONS.EN) {
    if (!dateString) return "";

    try {
        const normalizedDate = normalizeUTCString(dateString);

        // 使用实例级 locale，不影响全局状态
        // dayjs 的 .locale() 方法返回一个新实例，不会修改原实例或全局状态
        return dayjs.utc(normalizedDate).locale(locale).format(format);
    } catch (error) {
        console.error("[dateFormatterUtils] formatDateWithLocale 错误:", error, {
            dateString,
            format,
            locale,
        });
        return "";
    }
}

/**
 * 格式化为英文日期（航空业标准）
 * 这个函数保证输出英文格式，不受任何全局状态影响
 *
 * @param {string|Date} dateString - UTC 日期字符串
 * @param {string} format - 输出格式，默认为 DD-MMM
 * @returns {string} 英文格式的日期字符串
 */
export function formatDateEN(dateString, format = AVIATION_DATE_FORMATS.DD_MMM) {
    return formatDateWithLocale(dateString, format, LOCALE_OPTIONS.EN);
}

/**
 * 格式化为中文日期
 *
 * @param {string|Date} dateString - UTC 日期字符串
 * @param {string} format - 输出格式，默认为 M月D日
 * @returns {string} 中文格式的日期字符串
 */
export function formatDateZH(dateString, format = AVIATION_DATE_FORMATS.M_D_CN) {
    return formatDateWithLocale(dateString, format, LOCALE_OPTIONS.ZH_CN);
}

/**
 * 格式化时间（UTC）
 * 时间格式不受 locale 影响，但统一使用此函数以保持一致性
 *
 * @param {string|Date} dateString - UTC 日期时间字符串
 * @param {string} format - 输出格式，默认为 HH:mm
 * @returns {string} 格式化后的时间字符串
 */
export function formatTimeUTC(dateString, format = AVIATION_DATE_FORMATS.HH_MM) {
    if (!dateString) return "";

    try {
        const normalizedDate = normalizeUTCString(dateString);
        return dayjs.utc(normalizedDate).format(format);
    } catch (error) {
        console.error("[dateFormatterUtils] formatTimeUTC 错误:", error, { dateString, format });
        return "";
    }
}

/**
 * 使用 Intl API 格式化日期（浏览器原生，最可靠）
 * 这是终极后备方案，不依赖任何第三方库
 *
 * @param {string|Date} dateString - 日期字符串或 Date 对象
 * @param {Intl.DateTimeFormatOptions} options - Intl.DateTimeFormat 选项
 * @param {string} locale - BCP 47 语言标签，默认 en-US
 * @returns {string} 格式化后的日期字符串
 */
export function formatDateIntl(dateString, options, locale = "en-US") {
    if (!dateString) return "";

    try {
        const date = typeof dateString === "string" ? new Date(dateString) : dateString;
        if (isNaN(date.getTime())) return "";

        return new Intl.DateTimeFormat(locale, options).format(date);
    } catch (error) {
        console.error("[dateFormatterUtils] formatDateIntl 错误:", error, {
            dateString,
            options,
            locale,
        });
        return "";
    }
}

/**
 * 格式化月份名称（保证英文输出）
 * 使用 Intl API 作为最可靠的方案
 *
 * @param {string|Date} dateString - 日期字符串
 * @param {boolean} fullName - 是否使用完整月份名称，默认 false (短名称)
 * @returns {string} 英文月份名称
 */
export function formatMonthEN(dateString, fullName = false) {
    return formatDateIntl(dateString, { month: fullName ? "long" : "short" }, "en-US");
}

/**
 * 格式化月份和年份（保证英文输出）
 *
 * @param {string|Date} dateString - 日期字符串
 * @param {boolean} fullMonth - 是否使用完整月份名称
 * @returns {string} 格式如 "December 2025" 或 "Dec 2025"
 */
export function formatMonthYearEN(dateString, fullMonth = true) {
    return formatDateIntl(dateString, { month: fullMonth ? "long" : "short", year: "numeric" }, "en-US");
}

/**
 * 获取星期几（基于 UTC 日期）
 *
 * @param {string|Date} dateString - UTC 日期字符串
 * @returns {number} 星期几 (1-7，周一=1，周日=7)
 */
export function getDayOfWeekUTC(dateString) {
    if (!dateString) return "";

    try {
        const normalizedDate = normalizeUTCString(dateString);
        const dayIndex = dayjs.utc(normalizedDate).day(); // 0=周日, 1=周一, ..., 6=周六
        // 转换为航空业习惯：1=周一, 7=周日
        return dayIndex === 0 ? 7 : dayIndex;
    } catch (error) {
        console.error("[dateFormatterUtils] getDayOfWeekUTC 错误:", error, { dateString });
        return "";
    }
}

/**
 * 转换本地时区时间并格式化
 *
 * @param {string|Date} utcDateTime - UTC 日期时间字符串
 * @param {string} timezone - 目标时区 (IANA 时区名称)
 * @param {string} format - 输出格式
 * @param {string} locale - 目标 locale
 * @returns {string} 格式化后的本地时间字符串
 */
export function formatLocalTime(utcDateTime, timezone, format = AVIATION_DATE_FORMATS.HH_MM, locale = LOCALE_OPTIONS.EN) {
    if (!utcDateTime) return "";

    try {
        const normalizedDate = normalizeUTCString(utcDateTime);

        if (timezone === "UTC") {
            return dayjs.utc(normalizedDate).locale(locale).format(format);
        }

        return dayjs.utc(normalizedDate).tz(timezone).locale(locale).format(format);
    } catch (error) {
        console.error("[dateFormatterUtils] formatLocalTime 错误:", error, {
            utcDateTime,
            timezone,
            format,
            locale,
        });
        return "";
    }
}

/**
 * 专为邮件内容块设计的日期格式化函数
 * 根据业务规则自动选择 locale
 *
 * @param {string} dateString - UTC 日期字符串
 * @param {string} format - 输出格式
 * @param {boolean} useChineseLocale - 是否使用中文 locale
 * @returns {string} 格式化后的日期字符串
 */
export function formatDateForEmail(dateString, format, useChineseLocale = false) {
    const locale = useChineseLocale ? LOCALE_OPTIONS.ZH_CN : LOCALE_OPTIONS.EN;
    return formatDateWithLocale(dateString, format, locale);
}

/**
 * 格式化日期为 DD-MMM 格式（航空业表格标准）
 * 保证输出英文格式如 "04-Dec"
 *
 * @param {string|Date} dateString - UTC 日期字符串
 * @returns {string} DD-MMM 格式的日期字符串
 */
export function formatDateForTable(dateString) {
    return formatDateEN(dateString, AVIATION_DATE_FORMATS.DD_MMM);
}

/**
 * 格式化日期为 DDMMM 格式并转为大写
 * 用于邮件主题行等场景，如 "04DEC"
 *
 * @param {string|Date} dateString - UTC 日期字符串
 * @returns {string} DDMMM 格式的大写日期字符串
 */
export function formatDateForSubject(dateString) {
    return formatDateEN(dateString, AVIATION_DATE_FORMATS.DDMMM).toUpperCase();
}
